import React, { useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  FormHelperText,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Newspaper,
  Upload,
  Trash2,
  FileText,
  Image as ImageIcon,
  AlertCircle,
} from "lucide-react";
import { colors, customShadows } from "../../../../theme";
import {
  CommonDialog,
  CommonButton,
  CommonFormField,
  CommonInput,
  CommonEditor,
} from "../../ui";

/**
 * NewsForm Component
 * Comprehensive modal dialog for creating and editing news articles.
 */
const NewsForm = ({
  open,
  onClose,
  onSubmit,
  isEdit = false,
  title = isEdit ? "Edit News Article" : "Add News Article",
  subtitle = isEdit
    ? "Update the article content, headline, or media."
    : "Publish a new announcement or update for the platform.",
  newsTitle,
  setNewsTitle,
  file,
  setFile,
  existingImageUrl,
  setExistingImageUrl,
  editorState,
  setEditorState,
  loading = false,
  error = "",
  validated = false,
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleRemoveImage = () => {
    setFile(null);
    if (setExistingImageUrl) {
      setExistingImageUrl("");
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Determine current image preview source
  let previewSrc = null;
  if (file && file instanceof Blob) {
    try {
      previewSrc = URL.createObjectURL(file);
    } catch {
      previewSrc = null;
    }
  } else if (existingImageUrl && typeof existingImageUrl === "string") {
    previewSrc = existingImageUrl;
  }

  const isTitleInvalid = validated && (!newsTitle || newsTitle.trim() === "");
  const isContentInvalid =
    validated &&
    (!editorState ||
      !editorState.getCurrentContent ||
      !editorState.getCurrentContent().hasText());

  return (
    <CommonDialog
      open={open}
      // onClose={onClose}
      disableScrollLock={false}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: colors.primary.lighter,
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Newspaper size={24} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: colors.slate[800],
                fontSize: "1.25rem",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[500],
                  mt: 0.25,
                  fontSize: "0.85rem",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      }
      showCloseButton
      maxWidth="md"
      fullWidth
      dividers={false}
      titleSx={{
        px: { xs: 2.5, sm: 4 },
        py: 3,
        borderBottom: `1px solid ${colors.dashboard.cardBorder}`,
      }}
      contentSx={{
        p: 0,
      }}
      actions={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            width: "100%",
          }}
        >
          <CommonButton
            variant="cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </CommonButton>

          <CommonButton
            variant="primary"
            type="submit"
            form="news-article-form"
            loading={loading}
          >
            {isEdit ? "Update Article" : "Publish Article"}
          </CommonButton>
        </Box>
      }
      // actionsSx={{
      //   // px: { xs: 2.5, sm: 4 },
      //   // py: 2.5,
      //   // borderTop: `1px solid ${colors.dashboard.cardBorder}`,
      //   // bgcolor: colors.slate[50] || "#f8fafc",
      // }}
    >
      <Box
        component="form"
        id="news-article-form"
        onSubmit={onSubmit}
        noValidate
        sx={{
          p: { xs: 2.5, sm: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Error Alert Box */}
        {error && (
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              bgcolor: colors.error.lighter || "#fef2f2",
              border: `1px solid ${colors.error.border || "#fca5a5"}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: colors.error.main || "#dc2626",
            }}
          >
            <AlertCircle size={20} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* News Headline / Title */}
        <CommonFormField
          label="Article Headline"
          required
          icon={<FileText size={16} />}
          helperText="A captivating headline describing the platform update or news"
          error={isTitleInvalid}
        >
          <CommonInput
            placeholder="e.g., AutoBET Launches Instant Settlement Lottery Pools"
            value={newsTitle}
            onChange={(e) => setNewsTitle(e.target.value)}
            error={isTitleInvalid}
            fullWidth
          />
        </CommonFormField>

        {/* Cover Image Upload & Preview */}
        <CommonFormField
          label="Cover Image"
          icon={<ImageIcon size={16} />}
          helperText="Upload a high-resolution banner image (JPG, PNG, WebP up to 5MB)"
        >
          <Box
            sx={{
              border: `2px dashed ${
                previewSrc ? colors.primary.main : colors.slate[300]
              }`,
              borderRadius: "14px",
              p: 2.5,
              bgcolor: previewSrc ? colors.primary.lighter : colors.slate[50],
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": {
                borderColor: colors.primary.main,
                bgcolor: colors.primary.lighter,
              },
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              id="news-cover-input"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {previewSrc ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    component="img"
                    src={previewSrc}
                    alt="Preview"
                    sx={{
                      width: 90,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: "10px",
                      border: `1px solid ${colors.dashboard.cardBorder}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.slate[800] }}
                    >
                      {file ? file.name : "Cover Image Selected"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500] }}
                    >
                      {file
                        ? `${(file.size / 1024).toFixed(1)} KB`
                        : "Current Article Image"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CommonButton
                    variant="dangerText"
                    size="small"
                    startIcon={<Trash2 size={16} />}
                    onClick={handleRemoveImage}
                  >
                    Remove
                  </CommonButton>
                  <CommonButton
                    variant="outlined"
                    size="small"
                    startIcon={<Upload size={16} />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Replace Image
                  </CommonButton>
                </Box>
              </Box>
            ) : (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 2,
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    boxShadow: customShadows.cardFlat || "0 2px 6px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.primary.main,
                    mb: 1.5,
                  }}
                >
                  <Upload size={20} />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: colors.slate[800], mb: 0.5 }}
                >
                  Click to upload cover image
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.slate[400] }}
                >
                  Supports JPG, PNG, WEBP, or GIF (max. 5MB)
                </Typography>
              </Box>
            )}
          </Box>
        </CommonFormField>

        {/* Article Description / Rich Text WYSIWYG Editor */}
        <CommonFormField
          label="Article Content"
          required
          icon={<FileText size={16} />}
          helperText="Compose rich formatted body text with headings, lists, links, and styling"
          error={isContentInvalid ? "Article content cannot be empty." : undefined}
        >
          <CommonEditor
            editorState={editorState}
            onEditorStateChange={setEditorState}
            placeholder="Write the full news announcement and details here..."
            error={isContentInvalid}
          />
        </CommonFormField>
      </Box>
    </CommonDialog>
  );
};

NewsForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  newsTitle: PropTypes.string.isRequired,
  setNewsTitle: PropTypes.func.isRequired,
  file: PropTypes.any,
  setFile: PropTypes.func.isRequired,
  existingImageUrl: PropTypes.string,
  setExistingImageUrl: PropTypes.func,
  editorState: PropTypes.any.isRequired,
  setEditorState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  validated: PropTypes.bool,
};

export default NewsForm;
